import React, { useState, useCallback } from 'react';

import { Alert, Button, Card, Col, Form, Row, Spinner } from 'react-bootstrap';
import { css, StyleSheet } from 'aphrodite';
import { faTrash, faImage, faCloudUploadAlt } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Papa from 'papaparse';
import Swal from 'sweetalert2';

import { backgroundGrey1, bootstrapSecondary, grape, lightGrayBorder } from 'lib/css/colors';
import EntityType from 'lib/enums/EntityType';
import {
  GLOBAL_MANUFACTURERS_FOR_DROPDOWN,
  GLOBAL_VENDORS_FOR_DROPDOWN,
  INTERNAL_TOOLS_IMAGE_STATS,
  INTERNAL_TOOLS_DELETE_VENDOR_IMAGES,
  INTERNAL_TOOLS_DELETE_MANUFACTURER_IMAGES,
  INTERNAL_TOOLS_GET_SOURCES,
  getURL,
} from 'lib/networking/endpoints';
import SearchableScrollableSelect from 'components/shared/SearchableScrollableSelect';
import useGet from 'lib/hooks/useGet';
import usePost from 'lib/hooks/usePost';

// Constants and Enums
const ENTITY_TYPES = {
  DISTRIBUTOR: EntityType.DISTRIBUTOR,
  MANUFACTURER: EntityType.MANUFACTURER,
};

const IMAGE_TYPES = {
  ALL: 'all',
  THUMBNAILS: 'thumbnails',
  OTHER_IMAGES: 'other-images',
};

const ALERT_VARIANTS = {
  SUCCESS: 'success',
  WARNING: 'warning',
  DANGER: 'danger',
  INFO: 'info',
};

const MESSAGES = {
  STATS_LOADED: 'Statistics loaded successfully',
  SOURCES_LOADED: 'Sources loaded successfully',
  SELECT_ENTITY: 'Please select an entity',
  SELECT_SOURCE: 'Please select a source',
  CONFIRM_DELETION: 'Confirm Deletion',
  SUCCESS_TITLE: 'Success!',
  WARNING_TITLE: 'Warning!',
  ERROR_TITLE: 'Error!',
};

function DeleteEntityImages() {
  const [entityType, setEntityType] = useState(ENTITY_TYPES.DISTRIBUTOR);
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [imageType, setImageType] = useState(IMAGE_TYPES.ALL);
  const [filterBySource, setFilterBySource] = useState(false);
  const [statsParams, setStatsParams] = useState(null);
  const [stats, setStats] = useState(null);
  const [sourcesParams, setSourcesParams] = useState(null);
  const [sources, setSources] = useState(null);
  const [selectedSource, setSelectedSource] = useState(null);
  const [alert, setAlert] = useState({ show: false, message: '', variant: ALERT_VARIANTS.INFO });

  // CSV file upload state
  const [csvFilename, setCsvFilename] = useState('');
  const [skuList, setSkuList] = useState([]);
  const [csvError, setCsvError] = useState('');
  const [useSkuFilter, setUseSkuFilter] = useState(false);
  const fileInputRef = React.useRef(null);

  const showAlert = (message, variant = ALERT_VARIANTS.INFO) => {
    setAlert({ show: true, message, variant });
    setTimeout(() => setAlert({ show: false, message: '', variant: ALERT_VARIANTS.INFO }), 3000);
  };

  // Handle CSV file upload
  const handleCSVUpload = event => {
    const file = event.target.files[0];
    if (!file) return;

    setCsvFilename(file.name);
    setCsvError('');
    setUseSkuFilter(false);

    const reader = new FileReader();
    reader.onload = e => {
      const csvData = e.target.result;
      Papa.parse(csvData, {
        header: true,
        skipEmptyLines: true,
        complete: results => {
          // Look for a column named "SKUs" or similar
          const headers = results.meta.fields;
          const skuColumn = headers.find(
            header => header.toLowerCase() === 'skus' || header.toLowerCase() === 'sku',
          );

          if (skuColumn) {
            // Extract unique SKUs
            const skus = results.data
              .map(row => row[skuColumn])
              .filter(sku => sku && sku.trim() !== '');

            setSkuList(skus);
            setUseSkuFilter(true);
            Swal.fire({
              icon: 'success',
              title: 'SKUs Loaded',
              text: `Successfully loaded ${skus.length} SKUs from the CSV file.`,
            });
          } else {
            setCsvError('No column named "SKUs" or "SKU" found in the CSV file.');
          }
        },
        error: error => {
          setCsvError(`Error parsing CSV: ${error.message}`);
        },
      });
    };
    reader.onerror = () => {
      setCsvError('Error reading file');
    };
    reader.readAsText(file);
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  const resetFileUpload = () => {
    setCsvFilename('');
    setSkuList([]);
    setUseSkuFilter(false);
    setCsvError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // usePost for deleting vendor images
  const { postData: deleteVendorImagesPost, loading: deletingVendor } = usePost(
    INTERNAL_TOOLS_DELETE_VENDOR_IMAGES,
    res => {
      const result = res?.data?.data;
      if (result) {
        Swal.fire({
          title: 'Task Queued',
          html: `
            <p>${result.message}</p>
            <p>The deletion task has been queued and will be processed shortly. 
            You can monitor the progress in the Tasks page.</p>
          `,
          icon: ALERT_VARIANTS.SUCCESS,
          confirmButtonText: 'OK',
        });
        setStats(null);
        setStatsParams(null);
      }
    },
    error => {
      const errorMsg = error?.response?.data?.error || 'Failed to queue deletion task';
      Swal.fire({
        title: MESSAGES.ERROR_TITLE,
        text: errorMsg,
        icon: 'error',
        confirmButtonText: 'OK',
      });
    },
  );

  // usePost for deleting manufacturer images
  const { postData: deleteManufacturerImagesPost, loading: deletingManufacturer } = usePost(
    INTERNAL_TOOLS_DELETE_MANUFACTURER_IMAGES,
    res => {
      const result = res?.data?.data;
      if (result) {
        Swal.fire({
          title: 'Task Queued',
          html: `
            <p>${result.message}</p>
            <p>The deletion task has been queued and will be processed shortly. 
            You can monitor the progress in the Tasks page.</p>
          `,
          icon: ALERT_VARIANTS.SUCCESS,
          confirmButtonText: 'OK',
        });
        setStats(null);
        setStatsParams(null);
      }
    },
    error => {
      const errorMsg = error?.response?.data?.error || 'Failed to queue deletion task';
      Swal.fire({
        title: MESSAGES.ERROR_TITLE,
        text: errorMsg,
        icon: 'error',
        confirmButtonText: 'OK',
      });
    },
  );

  // Hook for fetching image stats
  const {
    data: statsData,
    loading: statsLoading,
    refetch: fetchStats,
  } = useGet(
    statsParams
      ? getURL(INTERNAL_TOOLS_IMAGE_STATS, {
          entity_type: statsParams.entity_type,
          entity_id: statsParams.entity_id,
        })
      : '',
    {},
    !statsParams, // skip if no params
    true, // lazy load
  );

  // Auto-fetch when statsParams changes
  React.useEffect(() => {
    if (statsParams) {
      fetchStats();
    }
  }, [statsParams, fetchStats]);

  // Handle stats data when it arrives
  React.useEffect(() => {
    if (statsData?.data) {
      setStats(statsData.data);
      showAlert(MESSAGES.STATS_LOADED, ALERT_VARIANTS.SUCCESS);
    }
  }, [statsData]);

  // Hook for fetching image sources
  const {
    data: sourcesData,
    loading: sourcesLoading,
    refetch: fetchSources,
  } = useGet(
    sourcesParams
      ? getURL(INTERNAL_TOOLS_GET_SOURCES, {
          entity_type: sourcesParams.entity_type,
          entity_id: sourcesParams.entity_id,
        })
      : '',
    {},
    !sourcesParams, // skip if no params
    true, // lazy load
  );

  // Auto-fetch when sourcesParams changes
  React.useEffect(() => {
    if (sourcesParams) {
      fetchSources();
    }
  }, [sourcesParams, fetchSources]);

  // Handle sources data when it arrives
  React.useEffect(() => {
    if (sourcesData?.data) {
      setSources(sourcesData.data);
      showAlert(MESSAGES.SOURCES_LOADED, ALERT_VARIANTS.SUCCESS);
    }
  }, [sourcesData]);

  const handleDeleteImages = useCallback(() => {
    if (!selectedEntity) {
      Swal.fire({
        title: MESSAGES.WARNING_TITLE,
        text: MESSAGES.SELECT_ENTITY,
        icon: 'warning',
        confirmButtonText: 'OK',
      });
      return;
    }

    // Check if source is selected when filtering by source
    if (filterBySource && !selectedSource) {
      Swal.fire({
        title: MESSAGES.WARNING_TITLE,
        text: MESSAGES.SELECT_SOURCE,
        icon: 'warning',
        confirmButtonText: 'OK',
      });
      return;
    }

    // Build confirmation message based on image type and source filter
    let imageTypeText = '';
    if (imageType === IMAGE_TYPES.ALL) {
      imageTypeText = 'all images';
    } else if (imageType === IMAGE_TYPES.THUMBNAILS) {
      imageTypeText = 'all thumbnail images';
    } else if (imageType === IMAGE_TYPES.OTHER_IMAGES) {
      imageTypeText = 'all non-thumbnail images';
    }

    let confirmMessage = `Are you sure you want to queue a deletion task for ${imageTypeText}`;
    if (filterBySource) {
      confirmMessage += ` with source '${selectedSource}'`;
    }
    if (useSkuFilter && skuList.length > 0) {
      confirmMessage += ` for ${skuList.length} specific SKUs`;
    }
    confirmMessage +=
      ` for ${selectedEntity.label}? ` +
      `The deletion will be processed asynchronously and may take several minutes.`;

    // Show confirmation dialog
    Swal.fire({
      title: MESSAGES.CONFIRM_DELETION,
      text: confirmMessage,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, queue deletion',
      cancelButtonText: 'Cancel',
    }).then(result => {
      if (result.isConfirmed) {
        // Build request body with appropriate parameters
        const requestBody = {
          entity_id: selectedEntity.value,
          entity_name: selectedEntity.label,
        };

        // Add image type filter
        if (imageType === IMAGE_TYPES.THUMBNAILS) {
          requestBody.thumbnails_only = true;
        } else if (imageType === IMAGE_TYPES.OTHER_IMAGES) {
          requestBody.other_images_only = true;
        }

        // Add source filter if enabled
        if (filterBySource && selectedSource) {
          requestBody.source = selectedSource;
        }

        // Add SKU filter if enabled
        if (useSkuFilter && skuList.length > 0) {
          requestBody.sku_list = skuList;
        }

        // Call appropriate endpoint based on entity type
        if (entityType === ENTITY_TYPES.DISTRIBUTOR) {
          deleteVendorImagesPost(requestBody);
        } else {
          deleteManufacturerImagesPost(requestBody);
        }
      }
    });
  }, [
    entityType,
    selectedEntity,
    imageType,
    filterBySource,
    selectedSource,
    useSkuFilter,
    skuList,
    deleteVendorImagesPost,
    deleteManufacturerImagesPost,
  ]);

  // Auto-fetch stats when entity is selected
  React.useEffect(() => {
    if (selectedEntity) {
      setStatsParams({
        entity_type: entityType,
        entity_id: selectedEntity.value,
      });
    }
  }, [selectedEntity, entityType]);

  // Auto-fetch sources when filterBySource is enabled and entity is selected
  React.useEffect(() => {
    if (filterBySource && selectedEntity) {
      setSourcesParams({
        entity_type: entityType,
        entity_id: selectedEntity.value,
      });
    }
  }, [filterBySource, selectedEntity, entityType]);

  const handleEntityTypeChange = entityTypeValue => {
    setEntityType(entityTypeValue);
    setSelectedEntity(null);
    setStats(null);
    setStatsParams(null);
    setSources(null);
    setSourcesParams(null);
    setSelectedSource(null);
    // Reset CSV upload
    resetFileUpload();
  };

  const deleting = deletingVendor || deletingManufacturer;

  return (
    <>
      {alert.show && (
        <Alert
          variant={alert.variant}
          onClose={() => setAlert({ ...alert, show: false })}
          dismissible
        >
          {alert.message}
        </Alert>
      )}

      <Card className={css(styles.card)}>
        <Card.Body>
          <Form>
            <Row>
              {/* Left Column - Form Fields */}
              <Col md={6}>
                <Form.Group className={css(styles.formGroup)}>
                  <Form.Label>Entity Type</Form.Label>
                  <div>
                    <Form.Check
                      inline
                      type="radio"
                      label="Distributor"
                      value={ENTITY_TYPES.DISTRIBUTOR}
                      checked={entityType === ENTITY_TYPES.DISTRIBUTOR}
                      onChange={e => handleEntityTypeChange(e.target.value)}
                    />
                    <Form.Check
                      inline
                      type="radio"
                      label="Manufacturer"
                      value={ENTITY_TYPES.MANUFACTURER}
                      checked={entityType === ENTITY_TYPES.MANUFACTURER}
                      onChange={e => handleEntityTypeChange(e.target.value)}
                    />
                  </div>
                </Form.Group>

                <Form.Group className={css(styles.formGroup)}>
                  <Form.Label>
                    Select{' '}
                    {entityType === ENTITY_TYPES.DISTRIBUTOR ? 'Distributor' : 'Manufacturer'}
                  </Form.Label>
                  {entityType === ENTITY_TYPES.DISTRIBUTOR ? (
                    <SearchableScrollableSelect
                      key="distributor-select"
                      initOption={selectedEntity}
                      onChange={option => {
                        setSelectedEntity(option);
                      }}
                      fetchUrl={GLOBAL_VENDORS_FOR_DROPDOWN}
                      optionMapper={vendor => ({
                        label: vendor.name,
                        value: vendor.id,
                      })}
                      autoFocus={false}
                      placeholder="Search and select distributor..."
                    />
                  ) : (
                    <SearchableScrollableSelect
                      key="manufacturer-select"
                      initOption={selectedEntity}
                      onChange={option => {
                        setSelectedEntity(option);
                      }}
                      fetchUrl={GLOBAL_MANUFACTURERS_FOR_DROPDOWN}
                      optionMapper={manufacturer => ({
                        label: manufacturer.name,
                        value: manufacturer.id,
                      })}
                      autoFocus={false}
                      placeholder="Search and select manufacturer..."
                    />
                  )}
                </Form.Group>

                <Form.Group className={css(styles.formGroup)}>
                  <Form.Label>Image Type</Form.Label>
                  <div>
                    <Form.Check
                      type="radio"
                      label="All Images"
                      value={IMAGE_TYPES.ALL}
                      checked={imageType === IMAGE_TYPES.ALL}
                      onChange={e => setImageType(e.target.value)}
                    />
                    <Form.Check
                      type="radio"
                      label="Thumbnails Only"
                      value={IMAGE_TYPES.THUMBNAILS}
                      checked={imageType === IMAGE_TYPES.THUMBNAILS}
                      onChange={e => setImageType(e.target.value)}
                    />
                    <Form.Check
                      type="radio"
                      label="Other Images Only (Non-Thumbnails)"
                      value={IMAGE_TYPES.OTHER_IMAGES}
                      checked={imageType === IMAGE_TYPES.OTHER_IMAGES}
                      onChange={e => setImageType(e.target.value)}
                    />
                  </div>
                </Form.Group>

                <Form.Group className={css(styles.formGroup)}>
                  <Form.Check
                    type="checkbox"
                    label="Filter by Source"
                    checked={filterBySource}
                    onChange={e => {
                      setFilterBySource(e.target.checked);
                      if (!e.target.checked) {
                        setSources(null);
                        setSourcesParams(null);
                        setSelectedSource(null);
                      }
                    }}
                  />
                </Form.Group>

                {filterBySource && (
                  <>
                    {sourcesLoading && (
                      <div className={css(styles.loadingContainer)}>
                        <Spinner animation="border" size="sm" /> Loading sources...
                      </div>
                    )}

                    {sources && sources.sources && sources.sources.length > 0 && (
                      <Form.Group className={css(styles.formGroup)}>
                        <Form.Label>Select Source to Delete</Form.Label>
                        <Form.Control
                          as="select"
                          value={selectedSource || ''}
                          onChange={e => setSelectedSource(e.target.value)}
                        >
                          <option value="">-- Select a source --</option>
                          {sources.sources.map(sourceObj => (
                            <option key={sourceObj.source} value={sourceObj.source}>
                              {sourceObj.source} ({sourceObj.count} images)
                            </option>
                          ))}
                        </Form.Control>
                      </Form.Group>
                    )}

                    {sources && sources.sources && sources.sources.length === 0 && (
                      <Alert variant="info" className={css(styles.warningBox)}>
                        No sources found for this {entityType}.
                      </Alert>
                    )}
                  </>
                )}

                {/* CSV Upload for SKU Filtering */}
                <Form.Group className={css(styles.formGroup)}>
                  <h5>Filter by SKUs</h5>
                  <p className={css(styles.subText)}>
                    Upload a CSV file with a column named SKUs to filter the deletion to specific
                    products
                  </p>

                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleCSVUpload}
                    accept=".csv"
                    style={{ display: 'none' }}
                  />

                  <div className={css(styles.dropzoneBox)} onClick={triggerFileInput}>
                    <div>
                      <div>
                        <FontAwesomeIcon icon={faCloudUploadAlt} />
                      </div>
                      <span>{csvFilename || 'Drop a CSV file here or click to upload'}</span>
                    </div>
                  </div>

                  {csvFilename && (
                    <Button variant="link" onClick={resetFileUpload} className="mt-2 p-0">
                      Remove file
                    </Button>
                  )}

                  {csvError && (
                    <Alert variant="danger" className="mt-2">
                      {csvError}
                    </Alert>
                  )}

                  {useSkuFilter && skuList.length > 0 && (
                    <Alert variant="success" className="mt-2">
                      {`${skuList.length} SKUs loaded from file.` +
                        ` Deletion will be filtered to only these SKUs.`}
                    </Alert>
                  )}
                </Form.Group>
              </Col>

              {/* Right Column - Statistics */}
              <Col md={6}>
                {selectedEntity && (
                  <Card className={css(styles.statsCard)}>
                    <Card.Body>
                      <h5 className={css(styles.statsTitle)}>
                        <FontAwesomeIcon icon={faImage} /> Statistics
                      </h5>
                      {statsLoading ? (
                        <div className={css(styles.loadingContainer)}>
                          <Spinner animation="border" size="sm" /> Loading statistics...
                        </div>
                      ) : stats ? (
                        <>
                          <Row>
                            <Col xs={6}>
                              <div className={css(styles.statBox, styles.statBoxLarge)}>
                                <div className={css(styles.statNumber)}>{stats.total_images}</div>
                                <div className={css(styles.statLabel)}>Total Images</div>
                              </div>
                            </Col>
                            <Col xs={6}>
                              <div className={css(styles.statBox, styles.statBoxLarge)}>
                                <div className={css(styles.statNumber)}>{stats.product_count}</div>
                                <div className={css(styles.statLabel)}>Total Products</div>
                              </div>
                            </Col>
                          </Row>
                          <Row className="mt-3">
                            <Col xs={6}>
                              <div className={css(styles.statBox)}>
                                <div className={css(styles.statNumber)}>
                                  {stats.thumbnail_count}
                                </div>
                                <div className={css(styles.statLabel)}>Thumbnails</div>
                              </div>
                            </Col>
                            <Col xs={6}>
                              <div className={css(styles.statBox)}>
                                <div className={css(styles.statNumber)}>
                                  {stats.other_images_count}
                                </div>
                                <div className={css(styles.statLabel)}>Other Images</div>
                              </div>
                            </Col>
                          </Row>
                        </>
                      ) : null}
                    </Card.Body>
                  </Card>
                )}
              </Col>
            </Row>

            {/* Delete Button */}
            <div className={css(styles.deleteButtonContainer)}>
              <Button
                variant="danger"
                size="lg"
                onClick={handleDeleteImages}
                disabled={!selectedEntity || deleting || statsLoading}
                className={css(styles.deleteButton)}
              >
                {deleting ? (
                  <>
                    <Spinner as="span" animation="border" size="sm" /> Deleting...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faTrash} /> Delete Images
                  </>
                )}
              </Button>
            </div>

            <Alert variant="warning" className={css(styles.warningBox)}>
              <strong>Warning:</strong>{' '}
              {imageType === IMAGE_TYPES.ALL &&
                !filterBySource &&
                `This action will delete all images associated with the selected ${entityType}.`}
              {imageType === IMAGE_TYPES.THUMBNAILS &&
                !filterBySource &&
                `This action will delete only thumbnail images for the selected ${entityType}.`}
              {imageType === IMAGE_TYPES.OTHER_IMAGES &&
                !filterBySource &&
                `This action will delete only non-thumbnail images for the selected ${entityType}.`}
              {imageType === IMAGE_TYPES.ALL &&
                filterBySource &&
                `This action will delete all images with the selected source for ${entityType}.`}
              {imageType === IMAGE_TYPES.THUMBNAILS &&
                filterBySource &&
                `This action will delete only thumbnail images with the selected ` +
                  `source for the ${entityType}.`}
              {imageType === IMAGE_TYPES.OTHER_IMAGES &&
                filterBySource &&
                `This action will delete only non-thumbnail images with the ` +
                  `selected source for the ${entityType}.`}{' '}
              The deletion will be processed asynchronously and cannot be undone. Please ensure you
              have selected the correct {filterBySource ? 'entity and source' : 'entity'}.
            </Alert>
          </Form>
        </Card.Body>
      </Card>
    </>
  );
}

const styles = StyleSheet.create({
  card: {
    marginTop: '1.5rem',
    padding: '1.5rem',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  },
  sectionTitle: {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    marginBottom: '1rem',
  },
  formGroup: {
    marginBottom: '1.5rem',
  },
  statsCard: {
    marginTop: '0',
    marginBottom: '1.5rem',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    border: `1px solid ${lightGrayBorder}`,
    height: 'fit-content',
    position: 'sticky',
    top: '20px',
  },
  statsTitle: {
    fontSize: '1.1rem',
    fontWeight: '600',
    marginBottom: '1.25rem',
    color: grape,
  },
  statBox: {
    textAlign: 'center',
    padding: '1rem',
    backgroundColor: backgroundGrey1,
    borderRadius: '8px',
    border: `1px solid ${lightGrayBorder}`,
  },
  statBoxLarge: {
    padding: '1.5rem',
  },
  statNumber: {
    fontSize: '2rem',
    fontWeight: 'bold',
    color: grape,
    marginBottom: '0.5rem',
  },
  statLabel: {
    fontSize: '0.9rem',
    color: bootstrapSecondary,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  loadingContainer: {
    textAlign: 'center',
    padding: '1rem',
    color: bootstrapSecondary,
  },
  deleteButtonContainer: {
    textAlign: 'center',
    marginTop: '2rem',
    marginBottom: '1.5rem',
  },
  deleteButton: {
    minWidth: '200px',
    fontWeight: '600',
  },
  warningBox: {
    marginTop: '1rem',
  },
  subText: {
    fontSize: '0.9rem',
    color: bootstrapSecondary,
    marginBottom: '0.75rem',
  },
  dropzoneBox: {
    border: `2px dashed ${lightGrayBorder}`,
    borderRadius: '8px',
    padding: '2rem',
    textAlign: 'center',
    cursor: 'pointer',
    backgroundColor: backgroundGrey1,
    transition: 'all 0.2s ease',
    ':hover': {
      borderColor: grape,
      backgroundColor: '#f8f7fc',
    },
  },
});

export default DeleteEntityImages;
